import React, { createContext, useContext, useState, ReactNode, useMemo } from 'react';
import { Toast, ToastContainer } from '../components/Toast';

interface ToastContextType {
  addToast: (toast: Omit<Toast, 'id'>) => string;
  removeToast: (id: string) => void;
  clearAll: () => void;
  success: (title: string, message?: string, duration?: number) => string;
  error: (title: string, message?: string, duration?: number) => string;
  info: (title: string, message?: string, duration?: number) => string;
  warning: (title: string, message?: string, duration?: number) => string;
  cartSuccess: (productName: string) => string;
  wishlistSuccess: (productName: string) => string;
  orderSuccess: (orderId: string) => string;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

interface ToastProviderProps {
  children: ReactNode;
}

export const ToastProvider: React.FC<ToastProviderProps> = ({ children }) => {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const addToast = React.useCallback((toast: Omit<Toast, 'id'>) => {
    const id = Math.random().toString(36).substr(2, 9);
    const newToast: Toast = {
      id,
      duration: 1000, // Default 1 second
      ...toast,
    };

    setToasts(prev => [...prev, newToast]);
    return id;
  }, []);

  const removeToast = React.useCallback((id: string) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  }, []);

  const clearAll = React.useCallback(() => {
    setToasts([]);
  }, []);

  // Convenience methods
  const success = React.useCallback((title: string, message?: string, duration?: number) => {
    return addToast({ type: 'success', title, message, duration });
  }, [addToast]);

  const error = React.useCallback((title: string, message?: string, duration?: number) => {
    return addToast({ type: 'error', title, message, duration });
  }, [addToast]);

  const info = React.useCallback((title: string, message?: string, duration?: number) => {
    return addToast({ type: 'info', title, message, duration });
  }, [addToast]);

  const warning = React.useCallback((title: string, message?: string, duration?: number) => {
    return addToast({ type: 'warning', title, message, duration });
  }, [addToast]);

  // Specialized cart notifications
  const cartSuccess = React.useCallback((productName: string) => {
    return addToast({
      type: 'success',
      title: 'Added to Cart! 🛒',
      message: `${productName} has been added to your cart`,
      duration: 1000,
    });
  }, [addToast]);

  const wishlistSuccess = React.useCallback((productName: string) => {
    return addToast({
      type: 'success',
      title: 'Added to Wishlist! ❤️',
      message: `${productName} has been added to your wishlist`,
      duration: 1000,
    });
  }, [addToast]);

  const orderSuccess = React.useCallback((orderId: string) => {
    return addToast({
      type: 'success',
      title: 'Order Placed! 📦',
      message: `Order ${orderId} has been placed successfully`,
      duration: 1000,
    });
  }, [addToast]);

  const value: ToastContextType = useMemo(() => ({
    addToast,
    removeToast,
    clearAll,
    success,
    error,
    info,
    warning,
    cartSuccess,
    wishlistSuccess,
    orderSuccess,
  }), [
    addToast,
    removeToast,
    clearAll,
    success,
    error,
    info,
    warning,
    cartSuccess,
    wishlistSuccess,
    orderSuccess,
  ]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastContainer toasts={toasts} onRemove={removeToast} />
    </ToastContext.Provider>
  );
};

export const useToast = (): ToastContextType => {
  const context = useContext(ToastContext);
  if (context === undefined) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};
